import { overlayInnerSvg } from '../../domain/annotation';
import type { VideoOverlay } from '../../domain/videoAnnotation';
import { markenBei } from '../../domain/videoAnnotationSvg';
import { thumbPfad, vorschauZeitMs } from '../../platform/video';
import { PhotoThumb } from './PhotoThumb';

/**
 * Die Vorschaukachel einer Videoaufnahme: das Standbild und die Marken, die dort gelten.
 *
 * ⚠️ **Befund des Nutzers vom 2026-09-20** — in der Kommentarliste war nichts von der
 * Annotation zu sehen. Zwei Gründe: die Kachel reichte `<video>-thumb.jpg` an `PhotoThumb`
 * weiter, und das sucht die **Foto**-Ebene `<pfad>-overlay.svg` — hier also
 * `<video>-thumb.jpg-overlay.svg`, eine Datei, die es nie gibt. Und am richtigen Ort läge
 * ohnehin nur JSON: die Videoebene ist zeitabhängig, ein fertiges SVG gibt es nicht.
 *
 * ⚠️ **Das Standbild ist nicht der erste Frame, sondern 0,5 s** (`VideoThumbnail.swift`,
 * gespiegelt in `platform/video.ts::vorschauZeitMs`). Ein Band, das vorher endet, gehört
 * deshalb nicht auf die Kachel — es wäre eine Markierung neben dem Bild.
 *
 * **Die Ebene kommt als Eigenschaft, nicht aus der Datei.** `MediaTabs` liest sie ohnehin für
 * die Blockzahl am Markierknopf; ein zweiter Lesevorgang je Kachel wäre dieselbe Datei
 * doppelt. Deshalb ist dieses Bauteil rein: gleiche Eingabe, gleiches Bild, ohne Effekt.
 */
export function VideoThumb({
  datei,
  ebene,
  durationSeconds,
}: {
  datei: string;
  /** Die gelesene Annotationsebene, oder `null` — dann bleibt es beim Standbild. */
  ebene: VideoOverlay | null;
  durationSeconds?: number;
}) {
  const marken = ebene ? markenBei(ebene, vorschauZeitMs(durationSeconds)) : [];

  /*
   * ⚠️ **Dasselbe Markup wie im Betrachter und in den Prüfvektoren** (`overlayInnerSvg`). Eine
   * eigene Zeichenroutine für die Kachel wäre eine dritte Quelle der Wahrheit — und die
   * kleinste von dreien fiele am längsten nicht auf.
   */
  const svg =
    ebene && marken.length > 0
      ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' +
        ebene.width + ' ' + ebene.height + '">' +
        overlayInnerSvg({ width: ebene.width, height: ebene.height, markers: marken }) +
        '</svg>'
      : undefined;

  return (
    <PhotoThumb
      path={thumbPfad(datei)}
      alt=""
      platzhalter="video"
      ebeneSvg={svg}
    />
  );
}
